import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACTIONS,
  EVENTS,
  EventData,
  Joyride,
  STATUS,
  TooltipRenderProps,
} from 'react-joyride';
import { useLocation } from 'react-router-dom';

import { Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  BlockRounded as BlockRoundedIcon,
  CheckRounded as CheckRoundedIcon,
  KeyboardArrowLeftRounded as KeyboardArrowLeftRoundedIcon,
  KeyboardArrowRightRounded as KeyboardArrowRightRoundedIcon,
  SkipNextRounded as SkipNextRoundedIcon,
} from '@mui/icons-material';

import { AppButton } from '../components/designe/buttons/app-button/AppButton';
import { LoginUserStatus, useAuth } from '../context/AuthContext';
import { useUserConfig } from '../context/UserConfigContext';
import { useWindowSize } from '../hooks/use-window-size';
import { getFirstMissingGateAgreementType } from '../redux/api/agreements/agreementPolicy';
import { useGetMyDetails } from '../redux/api/users/usersApi';
import {
  useGetMyOnboarding,
  usePostOnboardingBlockCompletion,
  usePostOnboardingSkipAll,
} from '../redux/api/users/usersApi';
import { Paths } from '../shared/paths';
import { getActiveOnboardingBlock } from './onboarding.config';

interface IOnboardingTooltipProps extends TooltipRenderProps {
  onSkipAll: () => void;
  backgroundColor: string;
  t: (key: string) => string;
}

const OnboardingTooltip = ({
  step,
  index,
  isLastStep,
  backProps,
  primaryProps,
  skipProps,
  onSkipAll,
  backgroundColor,
  t,
}: IOnboardingTooltipProps) => {
  return (
    <Stack
      sx={{
        backgroundColor: backgroundColor,
        borderRadius: '10px',
        boxShadow: 6,
        maxWidth: 360,
        p: 2.5,
      }}
      spacing={1.5}>
      <Typography variant="h3">
        {typeof step.title === 'string' ? step.title : t('onboarding.title')}
      </Typography>
      <Typography variant="text-sm" color="text.secondary">
        {typeof step.content === 'string'
          ? step.content
          : t('onboarding.fallbackContent')}
      </Typography>
      <Stack spacing={1} alignItems="flex-end">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <AppButton
            size="small"
            variant="outlined"
            color="primary"
            onClick={onSkipAll}
            startIcon={<BlockRoundedIcon />}>
            {t('onboarding.actions.skipAll')}
          </AppButton>
          <AppButton
            {...skipProps}
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<SkipNextRoundedIcon />}>
            {t('onboarding.actions.skip')}
          </AppButton>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          flexWrap="wrap">
          {index > 0 && (
            <AppButton
              {...backProps}
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<KeyboardArrowLeftRoundedIcon />}>
              {t('onboarding.actions.prev')}
            </AppButton>
          )}
          <AppButton
            {...primaryProps}
            size="small"
            variant="contained"
            color="primary"
            {...(isLastStep
              ? { startIcon: <CheckRoundedIcon /> }
              : { endIcon: <KeyboardArrowRightRoundedIcon /> })}>
            {isLastStep
              ? t('onboarding.actions.finish')
              : t('onboarding.actions.next')}
          </AppButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

interface IOnboardingProviderProps {
  children: React.ReactNode;
}

const ONBOARDING_DISABLED_PATHS = [Paths.completeRegistration];

const OnboardingProvider = ({ children }: IOnboardingProviderProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const { mobileView } = useWindowSize();
  const { loginUserStatus } = useAuth();
  const isLoggedIn = loginUserStatus === LoginUserStatus.loggedIn;
  const { data: me } = useGetMyDetails({
    skip: !isLoggedIn,
  });
  const hasPendingGateAgreement = Boolean(getFirstMissingGateAgreementType(me));
  const isOnboardingDisabledOnCurrentRoute = ONBOARDING_DISABLED_PATHS.includes(
    location.pathname,
  );
  const { options } = useUserConfig();
  const shouldRequestOnboarding =
    isLoggedIn &&
    !isOnboardingDisabledOnCurrentRoute &&
    Boolean(me?.shouldLoadOnboardingTooltips) &&
    !hasPendingGateAgreement &&
    options.onboardingEnabled;

  const { data: onboardingData, refetch } = useGetMyOnboarding(undefined, {
    skip: !shouldRequestOnboarding,
  });
  const [postOnboardingBlockCompletion] = usePostOnboardingBlockCompletion();
  const [postOnboardingSkipAll] = usePostOnboardingSkipAll();

  const activeBlock = useMemo(() => {
    if (
      isOnboardingDisabledOnCurrentRoute ||
      !onboardingData?.shouldLoadOnboardingTooltips
    ) {
      return null;
    }

    return getActiveOnboardingBlock(
      location.pathname,
      onboardingData.pendingBlockKeys ?? [],
    );
  }, [
    isOnboardingDisabledOnCurrentRoute,
    onboardingData?.pendingBlockKeys,
    onboardingData?.shouldLoadOnboardingTooltips,
    location.pathname,
  ]);

  const steps = useMemo(
    () => activeBlock?.getSteps({ mobileView, t }) ?? [],
    [activeBlock, mobileView, t],
  );
  const pauseRules = useMemo(
    () => activeBlock?.pauseRules ?? [],
    [activeBlock],
  );
  const completeOnClickTargets = useMemo(
    () => activeBlock?.completeOnClickTargets ?? [],
    [activeBlock],
  );
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [currentBlockKey, setCurrentBlockKey] = useState<string | null>(null);
  const [pauseResumeTarget, setPauseResumeTarget] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (hasPendingGateAgreement || !activeBlock || steps.length === 0) {
      setRun(false);
      setStepIndex(0);
      setCurrentBlockKey(null);
      setPauseResumeTarget(null);
      return;
    }

    setCurrentBlockKey(activeBlock.blockKey);
    setStepIndex(0);
    setPauseResumeTarget(null);
    setRun(true);
  }, [activeBlock, hasPendingGateAgreement, steps.length]);

  useEffect(() => {
    if (!pauseResumeTarget) {
      return;
    }

    const resumeIfReady = () => {
      if (!document.querySelector(pauseResumeTarget)) {
        return;
      }
      setPauseResumeTarget(null);
      setRun(true);
    };

    resumeIfReady();

    const observer = new MutationObserver(() => {
      resumeIfReady();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [pauseResumeTarget]);

  useEffect(() => {
    if ((!run && !pauseResumeTarget) || completeOnClickTargets.length === 0) {
      return;
    }

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const shouldComplete = completeOnClickTargets.some((selector) =>
        target?.closest?.(selector),
      );

      if (!shouldComplete) {
        return;
      }

      setRun(false);
      setPauseResumeTarget(null);
      void completeBlock('completed');
    };

    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [completeOnClickTargets, pauseResumeTarget, run]);

  const completeBlock = async (action: 'completed' | 'skipped') => {
    if (!currentBlockKey) {
      return;
    }

    await postOnboardingBlockCompletion({
      blockKey: currentBlockKey,
      action,
    }).unwrap();
    await refetch();
  };

  const skipAll = async () => {
    await postOnboardingSkipAll().unwrap();
    setRun(false);
    setPauseResumeTarget(null);
    await refetch();
  };

  const onJoyrideEvent = async (data: EventData) => {
    const { status, type, action, step, index } = data;
    const stepTarget = typeof step?.target === 'string' ? step.target : null;

    if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
      setStepIndex(Math.max(index - 1, 0));
      return;
    }

    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      if (index >= steps.length - 1) {
        setRun(false);
        setPauseResumeTarget(null);
        await completeBlock('completed');
        return;
      }

      const pauseRule = pauseRules.find(
        (rule) => rule.afterStepTarget === stepTarget,
      );
      const nextIndex = Math.min(index + 1, steps.length - 1);

      setStepIndex(nextIndex);
      if (pauseRule) {
        setPauseResumeTarget(pauseRule.resumeWhenTarget);
        setRun(false);
      }
      return;
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const pauseRule = pauseRules.find(
        (rule) => rule.afterStepTarget === stepTarget,
      );

      if (pauseRule && action !== ACTIONS.PREV) {
        const nextIndex = Math.min(index + 1, steps.length - 1);
        setStepIndex(nextIndex);
        setPauseResumeTarget(pauseRule.resumeWhenTarget);
        setRun(false);
        return;
      }

      if (pauseResumeTarget) {
        return;
      }

      if (action === ACTIONS.PREV) {
        setStepIndex(Math.max(index - 1, 0));
      } else {
        setStepIndex(Math.min(index + 1, steps.length - 1));
      }
      return;
    }

    if (status === STATUS.FINISHED) {
      setRun(false);
      setPauseResumeTarget(null);
      await completeBlock('completed');
      return;
    }

    if (status === STATUS.SKIPPED) {
      setRun(false);
      setPauseResumeTarget(null);
      await completeBlock('skipped');
    }
  };

  return (
    <>
      {children}
      <Joyride
        onEvent={onJoyrideEvent}
        continuous
        run={run}
        stepIndex={stepIndex}
        scrollToFirstStep
        options={{
          dismissKeyAction: false,
          overlayClickAction: false,
          buttons: ['back', 'primary', 'skip'],
          skipBeacon: true,
          zIndex: 1600,
          overlayColor: alpha(theme.palette.common.black, 0.5),
          backgroundColor: theme.palette.background.paper,
          arrowColor: theme.palette.background.paper,
        }}
        steps={steps}
        tooltipComponent={(props) => (
          <OnboardingTooltip
            {...props}
            onSkipAll={skipAll}
            backgroundColor={theme.palette.background.paper}
            t={t}
          />
        )}
      />
    </>
  );
};

export default OnboardingProvider;
